import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Training } from "@/components/tools/Training";
import { Button } from "@/components/ui/button";
import { Upload, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrainingUploadDialog } from "@/components/training/TrainingUploadDialog";
import { useAuth } from "@/contexts/AuthContext";
import { TrainingMaterial } from "@/services/trainingService";
import { canCreateTrainingMaterials } from "@/lib/rbac";

export default function TrainingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingMaterial, setEditingMaterial] =
    useState<TrainingMaterial | null>(null);

  const canCreateTraining = canCreateTrainingMaterials(user);

  const handleUploadSuccess = () => {
    setRefreshKey((prev) => prev + 1);
    setEditingMaterial(null);
  };

  const handleEditMaterial = (material: TrainingMaterial) => {
    setEditingMaterial(material);
    setUploadDialogOpen(true);
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 pt-6 space-y-4 max-w-4xl mx-auto">
        {/* Header with Upload Button - Matching Leads page style */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/tools")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <PageHeader
              title="Training"
              subtitle="Learning resources and materials"
            />
          </div>

          {/* Add Training Button - visible for users allowed to create materials */}
          {canCreateTraining && (
            <Button
              onClick={() => {
                setEditingMaterial(null);
                setUploadDialogOpen(true);
              }}
              className="shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
              size="sm"
            >
              <Upload className="h-4 w-4 mr-2" />
              <span>Add Training</span>
            </Button>
          )}
        </div>

        {/* Training content wrapped in error boundary */}
        <ErrorBoundary>
          <Training key={refreshKey} onEditMaterial={handleEditMaterial} />
        </ErrorBoundary>

        {/* Upload / Edit Dialog */}
        {canCreateTraining && (
          <TrainingUploadDialog
            open={uploadDialogOpen}
            onOpenChange={(open) => {
              setUploadDialogOpen(open);
              if (!open) setEditingMaterial(null);
            }}
            onSuccess={handleUploadSuccess}
            editMaterial={editingMaterial}
          />
        )}
      </div>
    </AppLayout>
  );
}
